import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
// Real prod leaf: the draggable/resizable browser-window video overlay.
import { VideoCallOverlay } from "@/components/bilateral/VideoCallOverlay";
import type { CallStatus } from "@/hooks/use-webrtc-peer"; // resolves to the type-only shim
import { track } from "./analytics";

/**
 * Part A video tile (Scope A3). Reuses the real VideoCallOverlay window for a LOCAL
 * self-view via getUserMedia + a placeholder remote (no WebRTC, no peer, no socket).
 * The camera stream never leaves the device. Degrades gracefully: if the camera is
 * declined or unavailable (e.g. iOS Safari quirks, or no camera at all), the overlay
 * shows an error/placeholder state instead of breaking.
 */
export function DemoVideoTile({ onClose }: { onClose: () => void }) {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  // "acquiring" until the camera attempt resolves; then "connected" either way so the
  // window reads as a normal call (placeholder self-tile if the camera is unavailable)
  // rather than an alarming "connection lost".
  const [status, setStatus] = useState<CallStatus>("acquiring");
  const [cameraOk, setCameraOk] = useState<boolean | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    track("video_started");
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("unsupported");
        const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = s;
        setLocalStream(s);
        setCameraOk(true);
        setStatus("connected");
      } catch {
        if (cancelled) return;
        setCameraOk(false); // graceful: placeholder self-tile, no error chrome
        setStatus("connected");
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const note =
    cameraOk === false
      ? "Camera unavailable or not allowed — on a real device your face shows here. It always stays on your device."
      : "Your camera stays on your device — nothing is sent.";

  // Portaled into a z-[70] stacking context at <body>: the vendored overlay is
  // `fixed z-40` but renders inside the page <main> (a z-1 context, app.css:186),
  // which traps it BELOW the z-60 demo banner — so when maximized its "Exit full
  // screen" top bar gets covered. The portal escapes <main>; z-[70] beats the
  // banner so the maximized controls are reachable. (Banner is only hidden while
  // maximized — a focused state that already reads as DEMO; PiP leaves it visible.)
  return createPortal(
    <div className="relative z-[70]">
      <VideoCallOverlay
        localStream={localStream}
        remoteStream={null}
        status={status}
        error={null}
        onEndCall={onClose}
        peerLabel="your client (demo)"
        corner="bottom-right"
      />
      {/* Persistent on-device note (Scope A3) — z-[60] keeps it above the overlay (z-40). */}
      <div className="pointer-events-none fixed inset-x-0 top-10 z-[60] flex justify-center px-4">
        <p className="max-w-[90vw] rounded-full border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)]/90 px-3 py-1 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-gold-soft)] backdrop-blur">
          {note}
        </p>
      </div>
    </div>,
    document.body,
  );
}
